/**
 * One entry of the item context menu. A disabled entry stays in the menu and
 * in its arrow-key order: `aria-disabled` (Chakra's `isFocusable`), the
 * reason as a tooltip and as the item's accessible description, the same
 * rule as the action bar's buttons.
 */

import { useId } from 'react';
import { Box, Flex, MenuCommand, MenuItem, Tooltip, VisuallyHidden } from '@chakra-ui/react';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { safeAnalytics } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { SubMenuItem } from '../../ui/SubMenuItem';
import { DANGER_ACTIONS, type ItemMenuEntry } from './itemMenuEntries';
import type { ItemMenuUiSurface } from './itemMenuStore';

/**
 * `fontSize`, not `boxSize`: MaterialIcon draws a ligature in a span whose
 * inline `1em` size beats Chakra's `boxSize` class, so only the font size
 * sizes it.
 */
const MENU_ICON_SIZE = '18px';

interface ItemMenuEntryItemProps {
  readonly entry: ItemMenuEntry;
  readonly label: string;
  readonly icon: string;
  readonly filled?: boolean;
  /**
   * A key that does this from a focused item, shown at the right edge. Visual
   * only (`aria-hidden`), so the item's name stays its label: MenuItem's own
   * `command` takes a plain string and cannot be hidden from the name.
   */
  readonly shortcut?: string;
  /** Sent with the chosen action; only the top bar's ⋮ sets it. */
  readonly surface?: ItemMenuUiSurface;
}

export function ItemMenuEntryItem({
  entry,
  label,
  icon,
  filled = false,
  shortcut,
  surface,
}: ItemMenuEntryItemProps) {
  const reasonId = useId();
  const enabled = entry.enabled ?? true;
  const danger = DANGER_ACTIONS.has(entry.id);
  const track = (option?: string) =>
    safeAnalytics.track(ANALYTICS_EVENTS.ITEM_CONTEXT_ACTION, {
      action: entry.id,
      ...(option && { option }),
      ...(surface && { surface }),
    });
  const iconNode = (
    <MaterialIcon
      name={icon}
      filled={filled}
      fontSize={MENU_ICON_SIZE}
      color={danger && enabled ? 'text.error' : 'inherit'}
    />
  );
  return (
    <>
      <Tooltip
        label={entry.reason}
        isDisabled={enabled || !entry.reason}
        hasArrow
        placement="right"
      >
        {/* The Box is the tooltip's trigger: Chakra writes its own
            aria-describedby onto the trigger (unset while closed), which
            would clobber the item's permanent description. */}
        <Box>
          {entry.children ? (
            // Expands in place (no flyout), so it fits a phone-width menu.
            <SubMenuItem
              label={label}
              icon={iconNode}
              isDisabled={!enabled}
              isFocusable
              aria-describedby={!enabled && entry.reason ? reasonId : undefined}
            >
              {entry.children.map((child) => (
                <MenuItem
                  key={child.id}
                  icon={<MaterialIcon name={child.icon} fontSize={MENU_ICON_SIZE} />}
                  onClick={() => {
                    track(child.id);
                    child.onSelect();
                  }}
                >
                  {child.label}
                </MenuItem>
              ))}
            </SubMenuItem>
          ) : (
            <MenuItem
              icon={iconNode}
              color={danger ? 'text.error' : undefined}
              isDisabled={!enabled}
              isFocusable
              aria-describedby={!enabled && entry.reason ? reasonId : undefined}
              onClick={
                enabled
                  ? () => {
                      track();
                      entry.onSelect();
                    }
                  : undefined
              }
            >
              {shortcut ? (
                <Flex as="span" align="center">
                  <Box as="span" flex={1}>
                    {label}
                  </Box>
                  <MenuCommand aria-hidden="true" ms={3}>
                    {shortcut}
                  </MenuCommand>
                </Flex>
              ) : (
                label
              )}
            </MenuItem>
          )}
        </Box>
      </Tooltip>
      {!enabled && entry.reason && <VisuallyHidden id={reasonId}>{entry.reason}</VisuallyHidden>}
    </>
  );
}
